import { redirect } from "next/navigation";
import { getSession } from "@/lib/auth/session";
import { LoginForm } from "./login-form";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { IconCheckCircle, IconSparkles } from "@/components/icons";

export const dynamic = "force-dynamic";

const HIGHLIGHTS = [
  "Vue cross-tenant des organisations clientes",
  "Supervision API + alertes monitoring",
  "Audit des actions admin",
  "Gestion des templates de rapports",
];

export default async function LoginPage({
  searchParams,
}: {
  searchParams: Promise<{ next?: string }>;
}) {
  const session = await getSession();
  if (session) redirect("/dashboard");

  const { next } = await searchParams;

  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      {/* Left panel — branding + tagline. Hidden on mobile. */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-sidebar p-12 text-sidebar-foreground lg:flex">
        {/* Subtle background pattern */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-sidebar-accent/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-accent/15 blur-3xl"
        />

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sidebar-accent/15 text-sidebar-accent ring-1 ring-sidebar-accent/30">
            <span className="text-base font-bold tracking-tighter">mf</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-sidebar-muted">
              missioflow
            </p>
            <p className="text-sm font-semibold tracking-tight">SuperAdmin</p>
          </div>
        </div>

        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-sidebar-active bg-sidebar-active/50 px-3 py-1 text-[11px] text-sidebar-muted">
            <IconSparkles size={12} className="text-sidebar-accent" />
            <span>Panel d&apos;administration globale</span>
          </div>
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Vue d&apos;ensemble du SaaS{" "}
            <span className="text-sidebar-accent">missioflow</span>
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-sidebar-muted">
            Supervision cross-tenant, audit des actions admin, gestion des
            templates de rapports. Réservé aux super-administrateurs.
          </p>

          <ul className="mt-8 space-y-2.5">
            {HIGHLIGHTS.map((h) => (
              <li
                key={h}
                className="flex items-start gap-3 text-sm text-sidebar-foreground/90"
              >
                <span className="mt-0.5 text-sidebar-accent">
                  <IconCheckCircle size={16} />
                </span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center justify-between text-[11px] text-sidebar-muted">
          <span className="font-mono">sysop.missioflow.fr</span>
          <span>
            © {new Date().getFullYear()} missioflow ·{" "}
            <span className="text-sidebar-accent">v0.1</span>
          </span>
        </div>
      </aside>

      {/* Right panel — form */}
      <main className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-sm">
          {/* Mobile-only branding */}
          <div className="mb-8 flex flex-col items-center gap-2 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sidebar text-sidebar-accent ring-1 ring-sidebar-accent/30">
              <span className="text-sm font-bold tracking-tighter">mf</span>
            </div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              missioflow
            </p>
            <p className="text-base font-semibold">SuperAdmin</p>
          </div>

          <Card className="mf-fade-in">
            <CardHeader>
              <CardTitle>Connexion</CardTitle>
              <CardDescription>
                Accès réservé aux super-administrateurs.
              </CardDescription>
            </CardHeader>
            <CardBody>
              <LoginForm next={next} />
            </CardBody>
          </Card>

          <p className="mt-6 text-center text-[11px] text-subtle-foreground">
            Tu n&apos;es pas super-admin ?{" "}
            <span className="text-muted-foreground">
              Connecte-toi sur ton sous-domaine tenant habituel.
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}
